import React, { type Dispatch, type SetStateAction } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { type Merge } from "type-fest";

import { css, cx } from "#styled-system/css";

export type PageNumberSelectorProperties = Merge<
  React.ComponentProps<"div">,
  {
    children?: never;
    /** TanStack Queryの`useInfiniteQuery().hasNextPage`を指定する。 */
    hasNextPage?: boolean;
    /** TanStack Queryの`useInfiniteQuery().isFetching`を指定する。 */
    isFetching: boolean;
    /** 次ページボタンを無効化する場合、`false`を指定する。 */
    nextPage?: boolean;
    /** 項目数 */
    numberOfItems: number;
    /** 次ページボタンが押された時のイベントハンドラー */
    onNextPage?: React.MouseEventHandler<HTMLButtonElement>;
    /** 前ページボタンが押された時のイベントハンドラー */
    onPreviousPage?: React.MouseEventHandler<HTMLButtonElement>;
    /** ページ番号 */
    pageNumber: number;
    /** ページサイズ */
    pageSize: number;
    /** 前ページボタンを無効化する場合、`false`を指定する。 */
    previousPage?: boolean;
    setPageNumber?: Dispatch<SetStateAction<number>>;
  }
>;

export function PageNumberSelector({
  className,
  hasNextPage,
  isFetching,
  nextPage = true,
  numberOfItems,
  onNextPage,
  onPreviousPage,
  pageNumber,
  pageSize,
  previousPage = true,
  setPageNumber,
  ...properties
}: PageNumberSelectorProperties) {
  return (
    <div
      {...properties}
      className={cx(css({ alignItems: "center", display: "flex", gap: "4px" }), className)}
    >
      <button
        className={css({
          alignItems: "center",
          aspectRatio: "1 / 1",
          backgroundColor: "transparent",
          border: "1px solid transparent",
          borderRadius: "65535px",
          color: "foreground",
          cursor: "pointer",
          display: "inline-flex",
          height: "40px",
          justifyContent: "center",
          overflow: "clip",
          transition: "all 0.2s ease-in-out",
          width: "auto",
          _disabled: {
            cursor: "not-allowed",
            opacity: 0.6,
          },
          _hover: {
            backgroundColor: "muted-background",
            borderColor: "border",
            opacity: 0.8,
          },
        })}
        disabled={!previousPage || pageNumber <= 0}
        onClick={function (event) {
          setPageNumber?.(function (pageNumber) {
            return Math.max(pageNumber - 1, 0);
          });
          onPreviousPage?.(event);
        }}
      >
        <LuChevronLeft />
      </button>
      <p
        className={css({
          flexGrow: 1,
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: 1,
          minWidth: 0,
          overflow: "visible",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        })}
      >
        {Math.min(pageNumber * pageSize + 1, numberOfItems)}
        <span
          className={
            isFetching && (pageNumber + 1) * pageSize >= numberOfItems
              ? css({
                  animation: "fade-out 0.5s ease-in infinite alternate",
                  filter: "blur(2px)",
                })
              : numberOfItems - pageNumber * pageSize <= 1
                ? css({ display: "none" })
                : undefined
          }
        >
          -{Math.min((pageNumber + 1) * pageSize, numberOfItems)}
        </span>
        &nbsp;
        <span
          className={
            isFetching
              ? css({
                  animation: "fade-out 0.5s ease-in infinite alternate",
                  filter: "blur(2px)",
                })
              : undefined
          }
        >
          /&nbsp;{numberOfItems}
          {hasNextPage && "~"}
        </span>
      </p>
      <button
        className={css({
          alignItems: "center",
          aspectRatio: "1 / 1",
          backgroundColor: "transparent",
          border: "1px solid transparent",
          borderRadius: "65535px",
          color: "foreground",
          cursor: "pointer",
          display: "inline-flex",
          height: "40px",
          justifyContent: "center",
          overflow: "clip",
          transition: "all 0.2s ease-in-out",
          width: "auto",
          _disabled: {
            cursor: "not-allowed",
            opacity: 0.6,
          },
          _hover: {
            backgroundColor: "muted-background",
            borderColor: "border",
            opacity: 0.8,
          },
        })}
        disabled={
          !nextPage ||
          (pageSize * (pageNumber + 1) >= numberOfItems && (!hasNextPage || isFetching))
        }
        onClick={function (event) {
          if (pageSize * (pageNumber + 1) < numberOfItems) {
            setPageNumber?.(function (pageNumber) {
              return Math.max(Math.min(pageNumber + 1, Math.ceil(numberOfItems / pageSize) - 1), 0);
            });
          }
          onNextPage?.(event);
        }}
      >
        <LuChevronRight />
      </button>
    </div>
  );
}
